import { useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  Stethoscope, Baby, Heart, Scissors, Microscope,
  ArrowRight, Phone, Building2, Clock, Shield, CheckCircle, X
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';

// Official department data with comprehensive descriptions
const departmentData = [
  {
    id: 'dept-general',
    name: 'General Medicine',
    icon: Stethoscope,
    description: 'The General Medicine Department at Baobab Hospital serves as the primary center for the diagnosis, treatment, and prevention of a wide range of adult health conditions. Our medical team provides comprehensive evaluation and management of both acute and chronic illnesses, including hypertension, diabetes, infections, respiratory conditions, and other common medical disorders. With a strong focus on preventive healthcare, the department encourages regular medical check-ups, early detection of diseases, and lifestyle guidance aimed at promoting long-term health and wellbeing.',
    detailedDescription: 'Through a patient-centered approach, our clinicians ensure that each patient receives individualized care based on their medical history, symptoms, and overall health condition. The department works closely with other specialized units within the hospital to ensure timely referrals, coordinated treatment plans, and effective follow-up care. This integrated approach ensures that patients receive continuous, safe, and high-quality medical care.',
    services: ['Routine Check-ups', 'Chronic Disease Management', 'Preventive Care', 'Health Screenings', 'Hypertension Management', 'Diabetes Care', 'Infection Treatment'],
  },
  {
    id: 'dept-pediatrics',
    name: 'Pediatrics',
    icon: Baby,
    description: 'The Pediatrics Department at Baobab Hospital is dedicated to providing specialized healthcare services for infants, children, and adolescents. Our pediatric team focuses on promoting healthy growth and development while diagnosing and treating a wide range of childhood illnesses. From routine health examinations and immunizations to the management of infections, nutritional concerns, and developmental conditions, the department provides compassionate and comprehensive care tailored to the needs of young patients.',
    detailedDescription: 'Understanding that children require a supportive and reassuring environment, our pediatric professionals work closely with parents and guardians to ensure proper medical guidance, preventive care, and timely treatment. The department emphasizes early detection of health issues, child wellness monitoring, and preventive health education to ensure that every child receives the best possible start in life.',
    services: ['Routine Health Examinations', 'Immunizations', 'Child Wellness Monitoring', 'Infection Management', 'Nutritional Guidance', 'Developmental Assessments', 'Preventive Health Education'],
  },
  {
    id: 'dept-maternity',
    name: 'Maternity & Obstetrics',
    icon: Heart,
    description: 'The Maternity and Obstetrics Department at Baobab Hospital provides complete maternal healthcare services, supporting women throughout pregnancy, childbirth, and the postnatal period. Our experienced medical team offers comprehensive antenatal care that includes routine check-ups, maternal health monitoring, nutritional guidance, and early detection of pregnancy-related complications. These services ensure that both mother and baby receive the highest level of care during pregnancy.',
    detailedDescription: 'During childbirth, the department is equipped to provide safe and supportive delivery services while ensuring the comfort and safety of mothers and newborns. After delivery, our team continues to provide postnatal care, breastfeeding guidance, and newborn health assessments. By combining clinical expertise with compassionate care, the department ensures a safe and positive maternity experience for families.',
    services: ['Antenatal Care', 'Maternal Health Monitoring', 'Safe Delivery Services', 'Postnatal Care', 'Breastfeeding Guidance', 'Newborn Health Assessments', 'Family Planning'],
  },
  {
    id: 'dept-surgery',
    name: 'Surgery',
    icon: Scissors,
    description: 'The Surgery Department at Baobab Hospital offers a wide range of surgical services performed by qualified medical professionals using modern medical techniques and safe surgical practices. The department is equipped to handle various general surgical procedures as well as emergency operations when immediate intervention is required. Our surgical team is committed to delivering effective treatment while ensuring patient safety at every stage of the surgical process.',
    detailedDescription: 'From pre-operative assessment and preparation to post-operative monitoring and recovery, patients receive comprehensive surgical care designed to achieve the best possible outcomes. The department works closely with anesthesiology, laboratory, and nursing teams to ensure smooth surgical procedures and proper recovery support, enabling patients to regain their health quickly and safely.',
    services: ['General Surgery', 'Emergency Operations', 'Pre-operative Assessment', 'Post-operative Care', 'Minor Surgical Procedures', 'Wound Care', 'Recovery Support'],
  },
  {
    id: 'dept-laboratory',
    name: 'Laboratory Services',
    icon: Microscope,
    description: 'The Laboratory Services Department at Baobab Hospital plays a critical role in supporting accurate diagnosis and effective treatment for patients. Equipped with modern diagnostic equipment and operated by skilled laboratory professionals, the department performs a wide range of medical tests including blood tests, infection screening, biochemical analysis, and other essential diagnostic investigations.',
    detailedDescription: 'Timely and reliable laboratory results allow physicians to make informed medical decisions and develop appropriate treatment plans for patients. The laboratory follows strict quality control procedures to ensure accuracy, safety, and efficiency in all testing processes. Through these services, the department contributes significantly to the hospital\'s commitment to providing high-quality, evidence-based healthcare.',
    services: ['Blood Tests', 'Infection Screening', 'Biochemical Analysis', 'Urinalysis', 'Pathology Services', 'Quality Control Testing', 'Rapid Diagnostics'],
  }
];

export default function Departments() {
  const [selectedDept, setSelectedDept] = useState(null);

  return (
    <div>
      {/* Hero Section with Image */}
      <section className="bg-gradient-to-br from-primary via-primary to-teal-dark" data-testid="departments-hero">
        <div className="flex flex-col lg:flex-row">
          {/* Image Side */}
          <div className="lg:w-1/2 h-64 lg:h-auto min-h-[300px]">
            <img 
              src="https://customer-assets.emergentagent.com/job_b1439859-e255-4ca3-943a-294da877a1c9/artifacts/c09dyda1_PAKIKANJI%20.jpeg"
              alt="Baobab Hospital Laboratory"
              className="w-full h-full object-cover"
            />
          </div>
          
          {/* Content Side - Left Aligned */}
          <div className="lg:w-1/2 py-20 md:py-28 px-6 lg:px-12 flex items-center">
            <div className="max-w-xl text-left">
              <p className="text-accent font-semibold mb-3 tracking-wide uppercase text-sm">Our Departments</p>
              <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6">
                Comprehensive Medical Departments
              </h1>
              <p className="text-white/80 text-lg md:text-xl leading-relaxed">
                Baobab Hospital operates through specialized departments to ensure coordinated and efficient healthcare delivery. Each department is staffed with experienced professionals dedicated to providing exceptional care.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Department Stats */}
      <section className="py-8 bg-warm border-b" data-testid="departments-stats">
        <div className="container-custom">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center">
                <Building2 className="w-6 h-6 text-primary" />
              </div>
              <div>
                <p className="font-heading text-2xl font-bold text-primary">5</p>
                <p className="text-slate-600 text-sm">Departments</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center">
                <Stethoscope className="w-6 h-6 text-primary" />
              </div>
              <div>
                <p className="font-heading text-2xl font-bold text-primary">40+</p>
                <p className="text-slate-600 text-sm">Medical Staff</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center">
                <Clock className="w-6 h-6 text-primary" />
              </div>
              <div>
                <p className="font-heading text-2xl font-bold text-primary">24/7</p>
                <p className="text-slate-600 text-sm">Service Hours</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center">
                <Shield className="w-6 h-6 text-primary" />
              </div>
              <div>
                <p className="font-heading text-2xl font-bold text-primary">2+</p>
                <p className="text-slate-600 text-sm">Years of Service</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Main Departments Grid */}
      <section className="py-16 md:py-24" data-testid="departments-grid">
        <div className="container-custom">
          <div className="text-center mb-12">
            <p className="text-primary font-semibold mb-2 tracking-wide uppercase text-sm">Healthcare Excellence</p>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-slate-800 mb-4">
              Our Medical Departments
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              Explore our specialized departments, each equipped with experienced professionals and modern facilities to deliver comprehensive healthcare services.
            </p>
          </div>

          <div className="space-y-6">
            {departmentData.map((dept, index) => (
              <Card 
                key={dept.id} 
                className="overflow-hidden hover:shadow-xl transition-all duration-300 border border-slate-100"
                data-testid={`department-card-${index}`}
              >
                <CardContent className="p-8">
                  {/* Header with Icon and Title */}
                  <div className="flex items-center gap-4 mb-6">
                    <div className="w-16 h-16 bg-gradient-to-br from-primary/20 to-primary/10 rounded-2xl flex items-center justify-center">
                      <dept.icon className="w-8 h-8 text-primary" />
                    </div>
                    <h3 className="font-heading text-2xl font-bold text-slate-800">{dept.name}</h3>
                  </div>
                  
                  {/* Description - Justified text */}
                  <div className="mb-6">
                    <p className="text-slate-600 leading-relaxed text-justify mb-4">
                      {dept.description}
                    </p>
                    {dept.detailedDescription && (
                      <p className="text-slate-600 leading-relaxed text-justify">
                        {dept.detailedDescription}
                      </p>
                    )}
                  </div>
                  
                  {/* Services */}
                  <div className="pt-6 border-t border-slate-100">
                    <p className="text-sm font-semibold text-slate-800 mb-3">Key Services:</p>
                    <div className="flex flex-wrap gap-2">
                      {dept.services.map((service, i) => (
                        <span 
                          key={i} 
                          className="px-3 py-1.5 bg-primary/5 text-primary text-sm rounded-full border border-primary/10"
                        >
                          {service}
                        </span>
                      ))}
                    </div>
                  </div>
                  
                  {/* Actions */}
                  <div className="mt-6 flex flex-wrap gap-3">
                    <Link to="/book-appointment">
                      <Button className="bg-primary hover:bg-primary-700">
                        Book Appointment
                        <ArrowRight className="w-4 h-4 ml-2" />
                      </Button>
                    </Link>
                    <Button 
                      variant="outline" 
                      onClick={() => setSelectedDept(dept)}
                      className="border-primary text-primary hover:bg-primary/5"
                    >
                      View Details
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section with Image */}
      <section className="py-0 bg-gradient-to-r from-primary to-teal-dark" data-testid="departments-cta">
        <div className="flex flex-col lg:flex-row">
          {/* Image Side */}
          <div className="lg:w-1/2 h-64 lg:h-auto">
            <img 
              src="https://customer-assets.emergentagent.com/job_b1439859-e255-4ca3-943a-294da877a1c9/artifacts/o4iir0r8_Baobab%20Hospital%20%2873%29.JPG.jpeg"
              alt="Baobab Hospital Ward"
              className="w-full h-full object-cover"
            />
          </div>
          
          {/* Content Side */}
          <div className="lg:w-1/2 flex items-center">
            <div className="p-8 lg:p-12">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-white mb-3">
                Need Medical Assistance?
              </h2>
              <p className="text-white/80 mb-8 max-w-lg">
                Our departments are ready to provide you with comprehensive healthcare services. Book an appointment or call us directly.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/book-appointment">
                  <Button size="lg" className="bg-white text-primary hover:bg-slate-100 rounded-lg px-8">
                    Book Appointment
                  </Button>
                </Link>
                <a href="tel:+255659984488">
                  <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10 rounded-lg px-8">
                    <Phone className="w-5 h-5 mr-2" />
                    Call Us
                  </Button>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Department Detail Modal */}
      {selectedDept && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setSelectedDept(null)}>
          <div 
            className="bg-white rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto"
            onClick={e => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-primary to-teal-dark p-6 rounded-t-2xl">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center">
                    <selectedDept.icon className="w-7 h-7 text-white" />
                  </div>
                  <h2 className="font-heading text-2xl font-bold text-white">{selectedDept.name}</h2>
                </div>
                <button 
                  onClick={() => setSelectedDept(null)}
                  className="w-10 h-10 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center text-white hover:bg-white/30 transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
            
            <div className="p-6">
              <div>
                <p className="text-slate-600 leading-relaxed mb-4 text-justify">
                  {selectedDept.description}
                </p>
                {selectedDept.detailedDescription && (
                  <p className="text-slate-600 leading-relaxed text-justify">
                    {selectedDept.detailedDescription}
                  </p>
                )}
              </div>
              
              <div className="mt-6 pt-6 border-t">
                <h3 className="font-semibold text-slate-800 mb-4">Services Offered</h3>
                <div className="grid sm:grid-cols-2 gap-3">
                  {selectedDept.services.map((service, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <CheckCircle className="w-5 h-5 text-primary flex-shrink-0" />
                      <span className="text-slate-600">{service}</span>
                    </div>
                  ))}
                </div>
              </div>
              
              <div className="mt-6 pt-6 border-t flex justify-end gap-3">
                <Link to="/book-appointment" onClick={() => setSelectedDept(null)}>
                  <Button className="bg-primary hover:bg-primary-700">
                    Book Appointment
                  </Button>
                </Link>
                <Link to="/find-doctor" onClick={() => setSelectedDept(null)}>
                  <Button variant="outline">
                    Find a Doctor
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
